import { Component, HostListener, Inject, Renderer2 } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ExternalCashInService } from './externalCashIn.service';
import { ToastrService } from 'ngx-toastr';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import { CASHIN_COUNTRY_FIELDS, CashInFields } from './externalCashInFields';
import { ActivatedRoute } from '@angular/router';
import { DepositPaymentRequest } from './externalCashIn.interface';
import { LogoService } from 'src/app/shared/services/logo.service';
import { DOCUMENT } from '@angular/common';

interface CheckoutCustomization {
  accountId?: string;
  colorButton?: string;
  colorBotton?: string;
  colorWindow?: string;
  urlImagemLogo?: string;
}

@Component({
  selector: 'app-externalCashIn',
  templateUrl: './externalCashIn.component.html',
  styleUrls: ['./externalCashIn.component.scss'],
})
export class ExternalCashInComponent {
  cashIn = new FormGroup({
    documento: new FormControl<string>('', [Validators.required]),
    email: new FormControl<string>('', [Validators.email]),
    nome: new FormControl<string>('', Validators.required),
    valor: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(1),
    ]),
    taxa: new FormControl<number>(0),
    valorTotal: new FormControl<number>(0),
    paymentMethods: new FormControl<string>('', Validators.required),
  });

  public CASHIN_COUNTRY_FIELDS = CASHIN_COUNTRY_FIELDS;

  public countryForm!: FormGroup;
  public currentCountryFields: CashInFields[] = [];

  public isCountryFormSubmitted = false;
  public checkoutUrl: string = '';
  public originCurrency: string = '';
  public transactionId = '';
  public idempotencyKey: string = '';
  public showCountryForm = false;
  public isSubmitted: boolean;
  public documentFeedBack: string;
  public nomeFeedBack: string;
  public valorFeedBack: string;
  public origemFeedBack: string;
  public responseMessage = '';
  public headerPhone: string = '';
  public depositTaxValue: number = 0;
  public depositTaxPercent: number = 0;
  public isLoaded: boolean;
  public originList: { name: string; value: string }[] = [];
  public touchStartX = 0;
  public touchEndX = 0;
  public mouseStartX = 0;
  public mouseEndX = 0;
  public currentStep = 0;
  public customization: CheckoutCustomization | null = null;
  public bankCodeOptions: { name: string; value: string }[] = [];
  public isLoadingBankCodes = false;

  public copDocumentTypes = [
    { value: 'CC', label: 'CC' },
    { value: 'TI', label: 'TI' },
    { value: 'CE', label: 'CE' },
    { value: 'PA', label: 'PA' },
    { value: 'NIT', label: 'NIT' },
  ];

  public penDocumentTypes = [
    { value: 'DNI', label: 'DNI' },
    { value: 'CE', label: 'CE' },
    { value: 'RUC', label: 'RUC' },
    { value: 'PAS', label: 'PAS' },
  ];

  public clpDocumentTypes = [
    { value: 'CI', label: 'CI' },
    { value: 'RUN', label: 'RUN' },
    { value: 'DNI', label: 'DNI' },
    { value: 'RUT', label: 'RUT' },
  ];

  private readonly PHP_DIRECT_METHODS = new Set<string>([
    'PH_QRIS',
    'PH_MAYA',
    'PH_DF_BANK',
    'PH_DF_WALLET',
    'PH_GRAB',
    'PH_GCASH',
  ]);



  constructor(
    private externalCashInService: ExternalCashInService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private logo: LogoService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.isSubmitted = false;
    this.documentFeedBack = '';
    this.nomeFeedBack = '';
    this.valorFeedBack = '';
    this.origemFeedBack = '';
    this.isLoaded = true;
    this.originList = [];
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent) {
    this.touchStartX = event.changedTouches[0].clientX;
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent) {
    this.touchEndX = event.changedTouches[0].clientX;
    this.handleSwipe(this.touchEndX - this.touchStartX);
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent) {
    this.mouseStartX = event.clientX;
  }

  @HostListener('mouseup', ['$event'])
  onMouseUp(event: MouseEvent) {
    this.mouseEndX = event.clientX;
    this.handleSwipe(this.mouseEndX - this.mouseStartX);
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((qp) => {
      const id = qp.get('id');
      if (id) {
        this.transactionId = id;
        this.getTransactionById(id);
      }
    });
  }

  get canGoToBankStep(): boolean {
    const oc = (this.originCurrency || '').toUpperCase();
    if (oc === 'MXN') return false;
    if (this.isPhpDirectMethod()) return false;
    return this.cashIn.valid;
  }

  private handleSwipe(deltaX: number): void {
    const threshold = 50;
    if (Math.abs(deltaX) < threshold) return;

    if (deltaX < 0) {
      if (this.currentStep === 0 && this.canGoToBankStep) {
        this.goToStep(1);
      }
    } else {
      if (this.currentStep === 1) {
        this.goToStep(0);
      }
    }
  }

  private isPhpDirectMethod(): boolean {
    const oc = (this.originCurrency || '').toUpperCase();
    if (oc !== 'PHP') return false;
    const method = this.upper(this.cashIn.controls.paymentMethods.value);
    return this.PHP_DIRECT_METHODS.has(method);
  }

  private formatPaymentMethodLabel(method: string): string {
    const oc = (this.originCurrency || '').toUpperCase();
    if (oc !== 'PHP') return method;

    let label = (method || '').toString().trim();
    const upper = label.toUpperCase();

    if (upper.startsWith('PH_')) {
      label = label.substring(3);
    } else if (upper.startsWith('PH')) {
      label = label.substring(2);
      if (label.startsWith('_')) label = label.substring(1);
    }

    return label.replace(/_/g, ' ');
  }

  public getTransactionById(id: string) {
    this.isLoaded = false;
    this.externalCashInService.getTransaction(id).subscribe({
      next: (res) => {
        this.idempotencyKey = res?.idempotencyKey ?? '';
        this.originCurrency = (
          res?.originCurrency ||
          this.originCurrency ||
          ''
        ).toUpperCase();
        this.headerPhone = (res?.phone ?? '').toString().trim();

        this.customization = res?.customizationColor || null;
        this.logo.setLogo(this.customization?.urlImagemLogo || null);
        this.applyPageBackground(this.customization?.colorBotton);

        this.cashIn.patchValue({
          nome: res?.name ?? '',
          documento: res?.document ?? '',
          email: res?.email ?? '',
          valor: res?.amount ?? null,
          paymentMethods: res?.origin ?? '',
        });

        const paymentMethods: string[] = res?.listPayMethod ?? [];
        this.originList = paymentMethods.map((m) => ({
          name: this.formatPaymentMethodLabel(m),
          value: m,
        }));

        if (this.originList.length) {
          this.cashIn.controls.paymentMethods.setValue(
            this.originList[0].value,
            { emitEvent: false }
          );
        } else {
          this.cashIn.controls.paymentMethods.reset(null, { emitEvent: false });
        }

        this.isLoaded = true;
      },
      error: (err) => {
        this.toastr.error(
          err?.error?.message || 'Não foi possível carregar o checkout.'
        );
        this.isLoaded = true;
      },
    });
  }

  onConfirmCheckout(): void {
    this.isSubmitted = true;
    if (this.cashIn.invalid) return;

    const oc = (this.originCurrency || '').toUpperCase();

    if (oc === 'MXN' || this.isPhpDirectMethod()) {
      const payload = this.buildBaseDepositPayment();
      this.sendDepositPayment(payload);
      return;
    }

    this.buildCountryForm();
    this.currentStep = 1;
    this.prefillForm();
  }

  private prefillForm(): void {
    if (!this.countryForm) return;

    const patch: any = {};
    const nome = (this.cashIn.controls.nome?.value ?? '').toString().trim();
    const doc = (this.cashIn.controls.documento?.value ?? '').toString().trim();
    const email = (this.cashIn.controls.email?.value ?? '').toString().trim();
    const phone = (this.headerPhone ?? '').toString().trim();

    const setIfHas = (key: string, val: any) => {
      if (val && this.countryForm.contains(key)) {
        patch[key] = val;
      }
    };

    switch ((this.originCurrency || '').toUpperCase()) {
      case 'CLP':
      case 'COP':
      case 'PEN':
        setIfHas('customerName', nome);
        setIfHas('documentNumber', doc);
        setIfHas('customerEmail', email);
        setIfHas('customerPhone', phone);
        break;
      default:
        break;
    }

    if (Object.keys(patch).length) {
      this.countryForm.patchValue(patch, { emitEvent: false });
      this.countryForm.updateValueAndValidity({ emitEvent: false });
    }
  }

  public get currencyPrefix(): string {
    const oc = (this.originCurrency || '').toUpperCase();
    switch (oc) {
      case 'BRL':
        return 'R$';
      case 'CLP':
      case 'COP':
      case 'MXN':
        return '$';
      case 'PEN':
        return 'S/';
      case 'PHP':
        return '₱';
      default:
        return '';
    }
  }
  

  private buildBaseDepositPayment(): DepositPaymentRequest {
    return {
      id: this.transactionId,
      origin: this.upper(this.cashIn.value.paymentMethods),
    };
  }

  public openCheckoutUrl(): void {
    if (this.checkoutUrl) {
      window.open(this.checkoutUrl, '_blank');
    }
  }

  public transformValue = (
    value: string | number | undefined | null
  ): string => {
    return String(Number(value) / 100);
  };

  public valorChanged() {
    const valor = Number(this.cashIn.controls.valor.value);
    this.cashIn.controls.valor.setValue(valor);
    let taxa = (valor * this.depositTaxPercent) / 100;
    taxa = valor > 0 ? taxa + this.depositTaxValue : 0;
    const valorTotal = valor > 0 ? valor - taxa : 0;
    this.cashIn.controls.taxa.setValue(taxa);
    this.cashIn.controls.valorTotal.setValue(valorTotal);
  }

  private buildCountryForm(): void {
    const oc = (this.originCurrency || '').toUpperCase();
    const baseCfg = this.CASHIN_COUNTRY_FIELDS[oc] || [];
    let fields: CashInFields[] = [...baseCfg];

    if (oc === 'COP') {
      const method = this.upper(this.cashIn.controls.paymentMethods.value);

      const removeField = (name: string) => {
        fields = fields.filter(f => f.name !== name);
      };

      const ensureField = (field: CashInFields) => {
        if (!fields.some(f => f.name === field.name)) {
          fields.push(field);
        }
      };

      if (method === 'PSE') {
        removeField('accountNo');
        ensureField({
          name: 'bankCode',
          label: 'common.bankCodeEx',
          validators: [Validators.required],
        });
      } else if (method === 'NEQUI') {
        removeField('bankCode');
        ensureField({
          name: 'accountNo',
          label: 'common.accountNo',
          validators: [Validators.required],
        });
      } else if (method === 'TRANSFIYA' || method === 'BREB') {
        removeField('accountNo');
        removeField('bankCode');
      }
    }

    this.currentCountryFields = fields;

    const group: any = {};
    fields.forEach((f) => (group[f.name] = new FormControl('', f.validators)));
    this.countryForm = new FormGroup(group);
    this.isCountryFormSubmitted = false;

    if (oc === 'COP' && this.countryForm.contains('documentType')) {
      const docTypeCtrl = this.countryForm.get('documentType');
      if (!docTypeCtrl?.value && this.copDocumentTypes.length) {
        this.countryForm.patchValue(
          { documentType: this.copDocumentTypes[0].value },
          { emitEvent: false }
        );
      }
    }

    if (oc === 'CLP' && this.countryForm.contains('documentType')) {
      const docTypeCtrl = this.countryForm.get('documentType');
      if (!docTypeCtrl?.value && this.clpDocumentTypes.length) {
        this.countryForm.patchValue(
          { documentType: this.clpDocumentTypes[0].value },
          { emitEvent: false }
        );
      }
    }

    if (oc === 'PEN' && this.countryForm.contains('documentType')) {
      const docTypeCtrl = this.countryForm.get('documentType');
      if (!docTypeCtrl?.value && this.penDocumentTypes.length) {
        this.countryForm.patchValue(
          { documentType: this.penDocumentTypes[0].value },
          { emitEvent: false }
        );
      }
    }

    this.bankCodeOptions = [];
    this.isLoadingBankCodes = false;

    if (oc === 'COP') {
      const method = this.upper(this.cashIn.controls.paymentMethods.value);
      if (method === 'PSE') {
        this.loadBankCodesForCurrentMethod();
      }
    }
  }

  confirmCountryForm(): void {
    this.isCountryFormSubmitted = true;
    if (this.countryForm.invalid) return;

    const base = this.buildBaseDepositPayment();
    const c = this.countryForm.value as any;

    switch ((this.originCurrency || '').toUpperCase()) {
      case 'CLP':
        base.extraClpCashIn = {
          customerName: c.customerName,
          customerEmail: c.customerEmail,
          documentNumber: c.documentNumber,
          documentType: c.documentType,
        };
        break;

      case 'COP': {
        const method = this.upper(this.cashIn.value.paymentMethods);
        const extra: any = {
          customerName: c.customerName,
          customerPhone: c.customerPhone,
          customerEmail: c.customerEmail,
          documentNumber: c.documentNumber,
          documentType: c.documentType,
        };

        if (method === 'PSE') {
          extra.bankCode = c.bankCode;
        } else if (method === 'NEQUI') {
          extra.accountNo = c.accountNo;
        }

        base.extraCopCashIn = extra;
        break;
      }

      case 'PEN':
        base.extraPenCashIn = {
          customerName: c.customerName,
          customerPhone: c.customerPhone,
          customerEmail: c.customerEmail,
          documentNumber: c.documentNumber,
          documentType: c.documentType,
        };
        break;
    }

    this.sendDepositPayment(base);
  }

  private loadBankCodesForCurrentMethod(): void {
    const oc = (this.originCurrency || '').toUpperCase();
    const method = this.upper(this.cashIn.controls.paymentMethods.value);

    if (oc !== 'COP' || method !== 'PSE' || !this.countryForm) return;

    this.isLoadingBankCodes = true;
    this.bankCodeOptions = [];

    this.externalCashInService.getBankCodes(oc, method).subscribe({
      next: (res) => {
        this.bankCodeOptions = (res?.bankCode || []).map((b: any) => ({
          name: String(b.bankName),
          value: String(b.id)
        }));
        this.isLoadingBankCodes = false;

        if (this.countryForm.contains('bankCode') && this.bankCodeOptions.length) {
          const current = this.countryForm.get('bankCode')?.value;
          if (!current) {
            this.countryForm.patchValue(
              { bankCode: this.bankCodeOptions[0].value },
              { emitEvent: false }
            );
          }
        }
      },
      error: () => {
        this.isLoadingBankCodes = false;
      }
    });
  }

  private sendDepositPayment(body: any): void {
    this.isLoaded = false;

    this.externalCashInService.updateTransaction(body).subscribe({
      next: (res) => {
        if (res.success) {
          this.checkoutUrl = res.urlCheckOut || '';
          this.toastr.success(res.message || 'Sucesso!');

          setTimeout(() => {
            if (this.checkoutUrl) {
              window.location.assign(this.checkoutUrl);
            }
          }, 50);
        } else {
          this.toastr.error(res.message || 'Erro');
          this.isLoaded = true;
        }
      },
      error: (err) => {
        this.toastr.error(err?.error?.message || 'Erro inesperado.');
        this.isLoaded = true;
      },
    });
  }

  public copyLinkToClipboard(): void {
    navigator.clipboard
      .writeText(this.checkoutUrl)
      .then(() =>
        this.toastr.success('Link copiado para a área de transferência!')
      )
      .catch(() => this.toastr.error('Erro ao copiar o link.'));
  }

  public formIsValid(): boolean {
    if (
      (this.cashIn.controls.documento.value!.length === 11 &&
        !cpf.isValid(this.cashIn.controls.documento.value!)) ||
      (this.cashIn.controls.documento.value!.length === 14 &&
        !cnpj.isValid(this.cashIn.controls.documento.value!))
    ) {
      this.cashIn.controls.documento.setErrors(Validators.required);
      this.documentFeedBack = 'Documento inválido!';
    }
    if (this.cashIn.controls.documento.hasError('required')) {
      this.documentFeedBack = 'Documento é obrigatório!';
    }
    if (this.cashIn.controls.nome.invalid) {
      this.nomeFeedBack = 'Nome é obrigatório!';
    }
    if (this.cashIn.controls.valor.hasError('min')) {
      this.valorFeedBack = 'Valor inválido - Valor mínimo R$ 1,00!';
    } else if (this.cashIn.controls.valor.invalid) {
      this.valorFeedBack = 'Valor é obrigatório!';
    }
    if (this.cashIn.controls.paymentMethods.hasError('required')) {
      this.origemFeedBack = 'Origem é obrigatória!';
    }
    return this.cashIn.valid;
  }

  private upper(v: any): string {
    return (v ?? '').toString().trim().toUpperCase();
  }

  goToStep(step: number): void {
    if (step === this.currentStep) return;

    if (step === 0) {
      this.currentStep = 0;
      this.isCountryFormSubmitted = false;
      return;
    }

    if (step === 1) {
      this.onConfirmCheckout();
    }
  }

  get buttonColor(): string {
    return this.customization?.colorButton || 'var(--primary-bg-color)';
  }

  get cardColor(): string {
    return this.customization?.colorWindow || '#ffffff';
  }

  get pageBackgroundColor(): string {
    return this.customization?.colorBotton || '#f5f5f5';
  }

  getStepIconStyle(stepIndex: number): { [key: string]: string } {
    if (this.currentStep === stepIndex) {
      return {
        'background-color': this.buttonColor,
        'border-color': this.buttonColor,
        color: '#fff',
      };
    }

    if (this.currentStep > stepIndex) {
      return {
        'background-color': '#16a34a',
        'border-color': '#16a34a',
        color: '#fff',
      };
    }

    return {};
  }

  private applyPageBackground(color?: string | null): void {
    const body = this.document.body;

    if (color) {
      this.renderer.setStyle(body, 'background-color', color);
    } else {
      this.renderer.removeStyle(body, 'background-color');
    }
  }
}
